import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useSelectedTrip } from '@/composables/trip'
import type {
  Transportation,
  TransportationGroup,
  TransportationGroupExtensionSuggestion,
} from '@/types/transportation'

export const useTransportationGroupStore = defineStore(
  'transportation-group',
  () => {
    const trip = useSelectedTrip()

    const groups = ref<Map<string, TransportationGroup>>(new Map())

    /**
     * Suggestion surfaced by the server (in the create-waypoint response)
     * when a newly inserted waypoint touches an existing driving group.
     * When set, the host component opens a modal asking the user whether
     * the new leg should be added to the group.
     */
    const pendingExtension = ref<TransportationGroupExtensionSuggestion | null>(
      null,
    )
    const isExtensionModalOpen = ref(false)
    const isExtensionSubmitting = ref(false)

    function notifyExtensionSuggestion(
      suggestion: TransportationGroupExtensionSuggestion | null | undefined,
    ) {
      if (!suggestion) {
        return
      }
      pendingExtension.value = suggestion
      isExtensionModalOpen.value = true
    }

    function dismissExtension() {
      isExtensionModalOpen.value = false
      pendingExtension.value = null
    }

    function setExtensionSubmitting(value: boolean) {
      isExtensionSubmitting.value = value
    }

    function setGroup(group: TransportationGroup) {
      // Reassign a fresh Map: Vue 2.7 does not instrument Map mutations, so an
      // in-place `.set()` would not notify the ref's dependents (see replaceAll).
      const next = new Map(groups.value)
      next.set(group._id, group)
      groups.value = next
    }

    function replaceAll(list: TransportationGroup[]) {
      const next = new Map<string, TransportationGroup>()
      for (const g of list) {
        next.set(g._id, g)
      }
      groups.value = next
    }

    function removeGroup(groupId: string) {
      // Reassign a fresh Map so the in-place delete is observed by Vue 2.7.
      const next = new Map(groups.value)
      next.delete(groupId)
      groups.value = next
    }

    function getGroupForTransportation(
      transportation: Transportation | { groupId?: string } | undefined,
    ): TransportationGroup | undefined {
      if (!transportation?.groupId) {
        return undefined
      }
      return groups.value.get(transportation.groupId)
    }

    function hasDepartureTransportation(group: TransportationGroup): boolean {
      const wps = trip.value?.waypoints ?? []
      const lastWaypointId = wps[wps.length - 1]?._id
      return !!lastWaypointId && group.fromWaypointIds.includes(lastWaypointId)
    }

    /** Pseudo-leg list derived from current trip waypoints. */
    const trippedLegs = computed(() => {
      const wps = trip.value?.waypoints ?? []
      return wps.map((wp, index) => ({
        fromWaypointId: wp._id,
        toWaypointId: wps[index + 1]?._id,
        fromName: wp.name,
        toName: wps[index + 1]?.name,
        isLast: index === wps.length - 1,
      }))
    })

    return {
      groups,
      trippedLegs,
      pendingExtension,
      isExtensionModalOpen,
      isExtensionSubmitting,
      setGroup,
      replaceAll,
      removeGroup,
      getGroupForTransportation,
      hasDepartureTransportation,
      notifyExtensionSuggestion,
      dismissExtension,
      setExtensionSubmitting,
    }
  },
)
